/**
 * Modal de copie de lien vers une partie partagée.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ShareType } from '../../types/library';
import { CloseIcon, LinkIcon } from '../Icons';
import { buildLibraryShareUrl } from '../../utils/libraryDeepLink';

interface ShareLinkModalProps {
  sharedGameId: number;
  shareType: ShareType;
  allowReshare: boolean;
  /** Vue auteur du partage (Mes partages) */
  isOwner?: boolean;
  onClose: () => void;
}

function accessHint(shareType: ShareType, allowReshare: boolean, isOwner: boolean): string {
  if (shareType === 'public') {
    return 'Partage public : toute personne disposant de ce lien peut ouvrir la partie.';
  }
  if (isOwner && !allowReshare) {
    return 'Partage privé verrouillé : seuls les destinataires désignés y accèdent via la bibliothèque ou le chat. Ils ne peuvent pas copier le lien.';
  }
  if (allowReshare) {
    return 'Partage privé : le lien ne fonctionne que pour les destinataires autorisés (joueurs ou équipes invités).';
  }
  return 'Ce partage privé ne peut pas être repartagé via un lien.';
}

export default function ShareLinkModal({
  sharedGameId,
  shareType,
  allowReshare,
  isOwner = false,
  onClose,
}: ShareLinkModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState(false);

  const url = buildLibraryShareUrl(sharedGameId, {
    section: shareType === 'public' ? 'shared_public' : 'shared_private',
    mode: 'replay',
  });

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      inputRef.current?.select();
    }
  }, [url]);

  return (
    <div className="library-modal-overlay" onClick={onClose}>
      <div className="library-modal library-share-link-modal" onClick={e => e.stopPropagation()}>
        <div className="library-modal-header">
          <h3>Lien de la partie</h3>
          <button type="button" className="library-modal-close" onClick={onClose} aria-label="Fermer">
            <CloseIcon size={16} />
          </button>
        </div>

        <div className="library-modal-body">
          <p className="library-share-link-hint">
            {accessHint(shareType, allowReshare, isOwner)}
          </p>

          <label className="library-modal-label" htmlFor="library-share-link-input">
            Lien direct
          </label>
          <div className="library-share-link-row">
            <input
              id="library-share-link-input"
              ref={inputRef}
              className="library-share-link-input"
              readOnly
              value={url}
              onFocus={e => e.target.select()}
            />
            <button
              type="button"
              className={`library-modal-btn primary library-share-link-copy${copied ? ' copied' : ''}`}
              onClick={() => { void handleCopy(); }}
            >
              <LinkIcon size={14} />
              {copied ? 'Copié ✓' : 'Copier'}
            </button>
          </div>
        </div>

        <div className="library-modal-footer">
          <div className="library-modal-buttons">
            <button type="button" className="library-modal-btn secondary" onClick={onClose}>
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
